import { api } from '../../../api/client';
import { returnDataBlob, dowloadPdf } from '../../../services/helpers';

// ---------------------------------------------------------------------------
// emision.api.js
// Capa de acceso a datos para la feature de Emisión de Certificados.
// ---------------------------------------------------------------------------

/** Traduce el tipo de frontend al tipo que espera el backend */
const getType = (type) => {
  if (!type) return type;
  const t = type.toString().toLowerCase();
  // Frontend keys
  if (t === 'businesses' || t === 'empresarial') return 'business';
  if (t === 'citizens' || t === 'ciudadano') return 'citizen';
  if (t === 'properties' || t === 'patrimonial') return 'property';
  // Already backend key (business, citizen, property)
  return t;
};

/**
 * Determina el parámetro de búsqueda en el historial según el tipo de certificado
 * y el valor buscado (CURP o RFC).
 * - citizens: usa curp si el valor tiene 18 chars, si no rfc
 * - properties: usa curp o rfc del propietario
 * - businesses: siempre rfc
 */
const getHistoryParam = (type, value = '') => {
  const v = (value || '').trim().toUpperCase();
  if (type === 'citizens' || type === 'citizen') {
    // CURP tiene 18 caracteres
    return v.length === 18 ? { key: 'curp', val: v } : { key: 'rfc', val: v };
  }
  if (type === 'properties' || type === 'property') {
    return v.length === 18 ? { key: 'curp', val: v } : { key: 'rfc', val: v };
  }
  // businesses siempre por RFC
  return { key: 'rfc', val: v };
};

const RENAPO_BIRTH_ENTITIES = {
  AS: 'Aguascalientes',
  BC: 'Baja California',
  BS: 'Baja California Sur',
  CC: 'Campeche',
  CS: 'Chiapas',
  CH: 'Chihuahua',
  DF: 'Ciudad de México',
  CL: 'Coahuila de Zaragoza',
  CM: 'Colima',
  DG: 'Durango',
  GT: 'Guanajuato',
  GR: 'Guerrero',
  HG: 'Hidalgo',
  JC: 'Jalisco',
  MC: 'México',
  MN: 'Michoacán de Ocampo',
  MS: 'Morelos',
  NT: 'Nayarit',
  NL: 'Nuevo León',
  OC: 'Oaxaca',
  PL: 'Puebla',
  QT: 'Querétaro',
  QR: 'Quintana Roo',
  SP: 'San Luis Potosí',
  SL: 'Sinaloa',
  SR: 'Sonora',
  TC: 'Tabasco',
  TS: 'Tamaulipas',
  TL: 'Tlaxcala',
  VZ: 'Veracruz de Ignacio de la Llave',
  YN: 'Yucatán',
  ZS: 'Zacatecas',
  NE: 'Nacido en el Extranjero',
};

const getBirthEntityName = (birthEntity) => {
  if (typeof birthEntity !== 'string') return birthEntity;
  const normalizedEntity = birthEntity.trim();
  return RENAPO_BIRTH_ENTITIES[normalizedEntity.toUpperCase()] || normalizedEntity;
};

const normalizeRenapoSex = (sex) => {
  if (typeof sex !== 'string') return sex;
  const normalizedSex = sex.trim().toUpperCase();
  if (normalizedSex === 'H') return 'Hombre';
  if (normalizedSex === 'M') return 'Mujer';
  return sex.trim();
};

const normalizeRenapoBirthDate = (birthDate) => {
  if (typeof birthDate !== 'string') return birthDate;
  const normalizedDate = birthDate.trim();
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(normalizedDate);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : normalizedDate;
};

export const mapRenapoPersonToEmissionData = (emissionData, personData) => ({
  ...emissionData,
  curp: personData.curp,
  fullName: [personData.name, personData.firstLastName, personData.secondLastName]
    .filter((part) => typeof part === 'string' && part.trim())
    .map((part) => part.trim())
    .join(' '),
  gender: normalizeRenapoSex(personData.sex),
  birthDate: normalizeRenapoBirthDate(personData.birthDate),
  state: getBirthEntityName(personData.birthEntity),
});

/**
 * Busca cualquier tipo de certificado (ciudadano, patrimonial, empresarial).
 * @param {{ type: string, option: string, value: string }} data
 */
const getAnyCertificate = (data) =>
  api.get(`/emisiones/search?tipo=${getType(data.type)}&${data.option}=${data.value}`);

/**
 * Emite un certificado. Puede retornar un PDF (blob) o un JSON indicando
 * que ya existe un certificado vigente.
 * Usamos api.getRaw porque necesitamos leer el Content-Type antes de parsear.
 */
const emitCertificate = async ({ searchData, rfc, prefix }) => {
  const tipo = getType(searchData.type);
  const identifier = rfc || searchData?.value || '';

  const response = await api.getRaw(
    `/emisiones/certificates/${encodeURIComponent(identifier)}/pdf?tipo=${tipo}`
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.message || `Error del servidor (${response.status})`;
    throw new Error(message);
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/pdf')) {
    return returnDataBlob(response, prefix);
  } else {
    const data = await response.json();
    return { folio: data.folio, vigencia: true, existeUnCertificadoVigente: true };
  }
};

/**
 * Crea un nuevo certificado incluso si ya existe uno vigente.
 */
const createNewCertificate = async (data, rfc, prefix) => {
  const tipo = getType(data.type);
  const identifier = rfc || data?.value || '';

  const response = await api.postRaw(
    `/emisiones/certificates/${encodeURIComponent(identifier)}/emision/newCertificate?tipo=${tipo}`,
    { createNewCertificate: true }
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.message || `Error del servidor (${response.status})`);
  }

  return returnDataBlob(response, prefix);
};

/**
 * Descarga el PDF de un certificado ya emitido por su folio.
 * @param {string} folio
 * @param {string} type
 * @returns {Promise<Blob>}
 */
const downloadCertificate = (folio, type) =>
  api.getBlob(
    `/emisiones/certificates/download/${folio}/pdf${type ? `?tipo=${getType(type)}` : ''}`
  );

/**
 * Descarga el XML de un certificado ya emitido por su folio.
 * @param {string} id
 * @param {string} type
 * @returns {Promise<Blob>}
 */
const downloadCertificateXml = (id, type) =>
  api.getBlob(
    `/emisiones/certificates/download/${id}/xml${type ? `?tipo=${getType(type)}` : ''}`
  );

export const emissionApi = {
  getAnyCertificate,
  emitCertificate,
  createNewCertificate,
  downloadCertificate,
  downloadCertificateXml,
};
